import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { keysV2, useFeatureSettings } from "../../hooks/v2";
import { api } from "../../lib/api";

/** [API property, database column] — booleans a company default can switch. */
const TENANT_BOOLS: Array<[string, string]> = [
  ["overrideEnabled", "override_enabled"], ["overrideRequiresPin", "override_requires_pin"], ["overrideRequiresRfid", "override_requires_rfid"],
  ["walletEnabled", "wallet_enabled"], ["enableInterBranchDebtTracking", "enable_inter_branch_debt_tracking"], ["settlePromoCredits", "settle_promo_credits"],
  ["requireWaiver", "require_waiver"], ["tieredPricingEnabled", "tiered_pricing_enabled"], ["bomEnabled", "bom_enabled"], ["reserveStockForBookings", "reserve_stock_for_bookings"],
];
/** The subset a single branch may override (null = inherit). */
const BRANCH_BOOLS = TENANT_BOOLS.filter(([p]) => !["enableInterBranchDebtTracking", "settlePromoCredits"].includes(p));

const label = (prop: string) => `features.${prop}`;

/**
 * Every enterprise feature is a switch: company defaults (owner) and per-branch overrides (admin). The server validates
 * combinations (e.g. overrides enabled with neither PIN nor badge is refused) and writes every change to the audit log.
 */
export function FeatureSettings() {
  const { t } = useTranslation();
  const { user, isAdmin } = useAuth();
  const { toast, toastError } = useToast();
  const qc = useQueryClient();
  const q = useFeatureSettings(isAdmin);
  const isOwner = user?.role === "owner";

  async function save(path: string, body: Record<string, unknown>) {
    try {
      await api.put(path, body);
      toast(t("features.saved"), "success");
      void qc.invalidateQueries({ queryKey: keysV2.featureSettings });
      void qc.invalidateQueries({ queryKey: keysV2.features });
      void qc.invalidateQueries({ queryKey: keysV2.override });
    } catch (e) {
      toastError(e);
    }
  }

  if (!isAdmin || !q.data) return null;
  const tenant = q.data.tenant as Record<string, unknown>;
  const monthField = (prop: string, col: string) => (
    <div key={prop} className="flex items-center justify-between gap-3">
      <label className="text-xs font-bold" htmlFor={`ft-${prop}`}>{t(label(prop))} <span className="font-medium text-gray-400">({t("features.months")})</span></label>
      <input id={`ft-${prop}`} className="field num !w-24 text-center" inputMode="numeric" disabled={!isOwner} defaultValue={tenant[col] === null || tenant[col] === undefined ? "" : String(tenant[col])} placeholder={t("features.never")}
        onBlur={(e) => { const v = e.target.value.trim(); const n = v === "" ? null : Number(v); if (n === null || (Number.isInteger(n) && n >= 0)) void save("/features/tenant", { [prop]: n }); }} />
    </div>
  );

  return (
    <section className="card space-y-4 p-5" aria-label={t("features.title")}>
      <div>
        <h2 className="text-sm font-black uppercase tracking-wide">{t("features.title")}</h2>
        <p className="text-xs text-gray-500">{t("features.subtitle")}</p>
      </div>

      <div className="space-y-2">
        <h3 className="text-xs font-black text-cosmic-purple">{t("features.tenant")}</h3>
        {!isOwner && <p className="text-[11px] font-semibold text-amber-700">{t("features.ownerOnly")}</p>}
        {TENANT_BOOLS.map(([prop, col]) => (
          <label key={prop} className="flex items-center justify-between gap-3 text-xs font-bold">
            <span>{t(label(prop))}</span>
            <input type="checkbox" role="switch" aria-label={t(label(prop))} className="h-4 w-4" disabled={!isOwner} checked={tenant[col] === true} onChange={(e) => void save("/features/tenant", { [prop]: e.target.checked })} />
          </label>
        ))}
        <div className="flex items-center justify-between gap-3 text-xs font-bold">
          <label htmlFor="ft-scope">{t("features.walletScope.label")}</label>
          <select id="ft-scope" className="field !w-auto" disabled={!isOwner} value={String(tenant.wallet_scope)} onChange={(e) => void save("/features/tenant", { walletScope: e.target.value })}>
            <option value="global">{t("features.walletScope.global")}</option>
            <option value="branch_specific">{t("features.walletScope.branch_specific")}</option>
          </select>
        </div>
        <div className="flex items-center justify-between gap-3 text-xs font-bold">
          <label htmlFor="ft-consume">{t("features.walletConsumeFirst")}</label>
          <select id="ft-consume" className="field !w-auto" disabled={!isOwner} value={String(tenant.wallet_consume_first)} onChange={(e) => void save("/features/tenant", { walletConsumeFirst: e.target.value })}>
            <option value="cash">{t("features.consume.cash")}</option>
            <option value="promo">{t("features.consume.promo")}</option>
          </select>
        </div>
        {monthField("walletExpiryMonths", "wallet_expiry_months")}
        {monthField("walletPromoExpiryMonths", "wallet_promo_expiry_months")}
      </div>

      <div className="space-y-3">
        <h3 className="text-xs font-black text-cosmic-purple">{t("features.branches")}</h3>
        {q.data.branches.map((b) => (
          <details key={b.branch_id} className="rounded-2xl border border-cosmic-silver p-3">
            <summary className="cursor-pointer text-xs font-black"><span className="num font-mono">{b.code}</span></summary>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {BRANCH_BOOLS.map(([prop, col]) => {
                const v = b[col] as boolean | null | undefined;
                return (
                  <label key={prop} className="flex items-center justify-between gap-2 text-[11px] font-bold">
                    <span>{t(label(prop))}</span>
                    <select aria-label={`${b.code} ${t(label(prop))}`} className="field !w-auto !py-1 text-[11px]" value={v === true ? "on" : v === false ? "off" : "inherit"}
                      onChange={(e) => void save(`/features/branches/${b.branch_id}`, { [prop]: e.target.value === "inherit" ? null : e.target.value === "on" })}>
                      <option value="inherit">{t("features.inherit")}</option>
                      <option value="on">{t("features.on")}</option>
                      <option value="off">{t("features.off")}</option>
                    </select>
                  </label>
                );
              })}
            </div>
          </details>
        ))}
      </div>
    </section>
  );
}
